import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Badge, Spinner, Alert, Button } from 'react-bootstrap';
import { FiLayers, FiArrowLeft, FiEdit } from 'react-icons/fi';
import { fetchTripSubcoverageByIdRequest } from '../../store/actions/tripsActions';
import './AdminTripSubcoverageDetail.css';

const AdminTripSubcoverageDetail = () => {
    const { subcoverageTripId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { selectedSubcoverageTrip } = useSelector((state) => state.trips);

    useEffect(() => {
        if (subcoverageTripId) {
            dispatch(fetchTripSubcoverageByIdRequest(subcoverageTripId));
        }
    }, [dispatch, subcoverageTripId]);

    const loading = selectedSubcoverageTrip.loading;
    const error = selectedSubcoverageTrip.error;
    const data = selectedSubcoverageTrip.data || {};

    const subcoverage = data.subcoverage_trip || {};
    const reference = data.reference || {};
    const partner = data.partner || {};

    const renderValue = (value, fallback = 'N/A') => {
        if (value === null || value === undefined || value === '') {
            return fallback;
        }
        return value;
    };

    return (
        <div className="dashboard-page admin-trip-subcoverage-detail">
            <Card className="mb-3">
                <Card.Body className="d-flex justify-content-between align-items-center">
                    <div>
                        <h2 className="page-title">
                            <FiLayers className="me-2" />
                            Détail sous-couverture séjour
                        </h2>
                        <p className="text-muted mb-0">
                            Consultez les informations complètes de la sous-couverture associée au séjour.
                        </p>
                    </div>
                    <div className="d-flex gap-2">
                        <Button variant="outline-primary" onClick={() => navigate(`/admin/trips/subcoverages/${subcoverageTripId}/edit`)}>
                            <FiEdit className="me-1" /> Modifier
                        </Button>
                        <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                            <FiArrowLeft className="me-1" /> Retour
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            {loading && (
                <div className="text-center my-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            )}

            {!loading && error && (
                <Alert variant="danger">{error}</Alert>
            )}

            {!loading && !error && data && (
                <>
                    {/* Section Informations générales */}
                    <Card className="mb-3">
                        <Card.Header>
                            <h5 className="mb-0">Informations générales</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <div className="detail-label">Code sous-couverture</div>
                                    <div className="detail-value" style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{renderValue(subcoverage.subcoverage_trip_code)}</div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Type de déclencheur</div>
                                    <div className="detail-value">
                                        <Badge bg="info" text="dark" className="text-capitalize">
                                            {renderValue(subcoverage.trigger_type)}
                                        </Badge>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Type ID Number</div>
                                    <div className="detail-value">{renderValue(subcoverage.type_id_number)}</div>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <div className="detail-label">Unité</div>
                                    <div className="detail-value">{renderValue(subcoverage.unit)}</div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Trigger inférieur</div>
                                    <div className="detail-value">{renderValue(subcoverage.trigger_low)}</div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Heures déclenchement</div>
                                    <div className="detail-value">{renderValue(subcoverage.trigger_number_hours)}</div>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <div className="detail-label">Trigger heure inférieur</div>
                                    <div className="detail-value">{renderValue(subcoverage.prcp_trigger_hour_inf)}</div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4}>
                                    <div className="detail-label">Ratio prix Orakle (excl. TSCA)</div>
                                    <div className="detail-value">{renderValue(subcoverage.orakle_price_ratio_excl_tsca)}</div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Type de couverture</div>
                                    <div className="detail-value">
                                        <Badge bg="secondary" className="text-capitalize">
                                            {renderValue(subcoverage.coverage_type)}
                                        </Badge>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Type de distribution</div>
                                    <div className="detail-value">
                                        <Badge bg="secondary" className="text-capitalize">
                                            {renderValue(subcoverage.distribution_type)}
                                        </Badge>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Plan de paiement</div>
                                    <div className="detail-value">
                                        <Badge bg="secondary" className="text-capitalize">
                                            {renderValue(subcoverage.payment_schedule)}
                                        </Badge>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Base de calcul du remboursement</div>
                                    <div className="detail-value">
                                        {subcoverage.refund_calculation_basis ? (
                                            <Badge bg="secondary" className="text-capitalize">
                                                {subcoverage.refund_calculation_basis}
                                            </Badge>
                                        ) : (
                                            renderValue(subcoverage.refund_calculation_basis)
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Section Période et horaires */}
                    <Card className="mb-3">
                        <Card.Header>
                            <h5 className="mb-0">Période et horaires</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <div className="detail-label">Période couverte</div>
                                    <div className="detail-value">
                                        {renderValue(subcoverage.sub_coverage_start_date)} &rarr; {renderValue(subcoverage.sub_coverage_end_date)}
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Horaires journée</div>
                                    <div className="detail-value">
                                        {renderValue(subcoverage.day_start_time)} - {renderValue(subcoverage.day_end_time)}
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Date effective début</div>
                                    <div className="detail-value">
                                        {subcoverage.effective_start_date !== null && subcoverage.effective_start_date !== undefined 
                                            ? (subcoverage.effective_start_date > 0 ? `+${subcoverage.effective_start_date}` : subcoverage.effective_start_date)
                                            : 'N/A'}
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Date effective fin</div>
                                    <div className="detail-value">
                                        {subcoverage.effective_end_date !== null && subcoverage.effective_end_date !== undefined 
                                            ? (subcoverage.effective_end_date < 0 ? subcoverage.effective_end_date : `+${subcoverage.effective_end_date}`)
                                            : 'N/A'}
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Section Timing */}
                    {(subcoverage.trigger_type === 'forecast' || subcoverage.trigger_type === 'forecast and observation') && (
                        <Card className="mb-3">
                            <Card.Header>
                                <h5 className="mb-0">Timing Forecast</h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <div className="detail-label">Heure de timing forecast</div>
                                        <div className="detail-value">{renderValue(subcoverage.forecast_timing_hour)}</div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="detail-label">Jour de timing forecast</div>
                                        <div className="detail-value">{renderValue(subcoverage.forecast_timing_day)}</div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}

                    {(subcoverage.trigger_type === 'observation' || subcoverage.trigger_type === 'forecast and observation') && (
                        <Card className="mb-3">
                            <Card.Header>
                                <h5 className="mb-0">Timing Observation</h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <div className="detail-label">Heure de timing observation</div>
                                        <div className="detail-value">{renderValue(subcoverage.observation_timing_hour)}</div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="detail-label">Jour de timing observation</div>
                                        <div className="detail-value">{renderValue(subcoverage.observation_timing_day)}</div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Section Localisation */}
                    <Card className="mb-3">
                        <Card.Header>
                            <h5 className="mb-0">Localisation</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <div className="detail-label">Ville</div>
                                    <div className="detail-value">{renderValue(subcoverage.city)}</div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Pays</div>
                                    <div className="detail-value">{renderValue(subcoverage.country)}</div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Code postal</div>
                                    <div className="detail-value">{renderValue(subcoverage.zip_code)}</div>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={12}>
                                    <div className="detail-label">Adresse activité</div>
                                    <div className="detail-value">{renderValue(subcoverage.activity_address)}</div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <div className="detail-label">Longitude</div>
                                    <div className="detail-value" style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{renderValue(subcoverage.geo_longitude)}</div>
                                </Col>
                                <Col md={6}>
                                    <div className="detail-label">Latitude</div>
                                    <div className="detail-value" style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{renderValue(subcoverage.geo_latitude)}</div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Section Indemnisation */}
                    <Card className="mb-3">
                        <Card.Header>
                            <h5 className="mb-0">Indemnisation</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <div className="detail-label">Forfait (lump sum)</div>
                                    <div className="detail-value">
                                        <Badge bg={subcoverage.is_lump_sum ? 'success' : 'secondary'}>
                                            {subcoverage.is_lump_sum ? 'Oui' : 'Non'}
                                        </Badge>
                                    </div>
                                </Col>
                            </Row>

                            {subcoverage.is_lump_sum ? (
                                <>
                                    <Row className="mb-3">
                                        <Col md={4}>
                                            <div className="detail-label">Forfait maintien</div>
                                            <div className="detail-value">{renderValue(subcoverage.lump_sum_maintain)}</div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="detail-label">Forfait annulation</div>
                                            <div className="detail-value">{renderValue(subcoverage.lump_sum_cancellation)}</div>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={4}>
                                            <div className="detail-label">Part cash maintien</div>
                                            <div className="detail-value">{renderValue(subcoverage.cash_share_maintain)}</div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="detail-label">Part cash annulation</div>
                                            <div className="detail-value">{renderValue(subcoverage.cash_share_cancellation)}</div>
                                        </Col>
                                    </Row>
                                </>
                            ) : (
                                <>
                                    <Row className="mb-3">
                                        <Col md={4}>
                                            <div className="detail-label">Ratio SIV maintenu</div>
                                            <div className="detail-value">{renderValue(subcoverage.siv_ratio_maintained)}</div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="detail-label">Ratio SIV annulé</div>
                                            <div className="detail-value">{renderValue(subcoverage.siv_ratio_cancelled)}</div>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={4}>
                                            <div className="detail-label">Part cash maintien</div>
                                            <div className="detail-value">{renderValue(subcoverage.cash_share_maintain)}</div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="detail-label">Part cash annulation</div>
                                            <div className="detail-value">{renderValue(subcoverage.cash_share_cancellation)}</div>
                                        </Col>
                                    </Row>
                                </>
                            )}

                            <Row className="mb-3 mt-3">
                                <Col md={6}>
                                    <div className="detail-label">Diviser voucher/cash maintien</div>
                                    <div className="detail-value">
                                        <Badge bg={subcoverage.split_voucher_cash_maintain ? 'success' : 'secondary'}>
                                            {subcoverage.split_voucher_cash_maintain ? 'Oui' : 'Non'}
                                        </Badge>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="detail-label">Diviser voucher/cash annulation</div>
                                    <div className="detail-value">
                                        <Badge bg={subcoverage.split_voucher_cash_cancellation ? 'success' : 'secondary'}>
                                            {subcoverage.split_voucher_cash_cancellation ? 'Oui' : 'Non'}
                                        </Badge>
                                    </div>
                                </Col>
                            </Row>

                            {subcoverage.split_voucher_cash_maintain && (
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <div className="detail-label">Part voucher maintien</div>
                                        <div className="detail-value">{renderValue(subcoverage.voucher_share_maintain)}</div>
                                    </Col>
                                </Row>
                            )}

                            {subcoverage.split_voucher_cash_cancellation && (
                                <Row>
                                    <Col md={6}>
                                        <div className="detail-label">Part voucher annulation</div>
                                        <div className="detail-value">{renderValue(subcoverage.voucher_share_cancellation)}</div>
                                    </Col>
                                </Row>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Section Sources de données */}
                    <Card className="mb-3">
                        <Card.Header>
                            <h5 className="mb-0">Sources de données</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <div className="detail-label">Source données maintien</div>
                                    <div className="detail-value">
                                        <Badge bg="info" text="dark">
                                            {renderValue(subcoverage.maintain_data_source)}
                                        </Badge>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="detail-label">Source données annulation</div>
                                    <div className="detail-value">
                                        <Badge bg="info" text="dark">
                                            {renderValue(subcoverage.cancellation_data_source)}
                                        </Badge>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Section Paramètres météorologiques */}
                    {subcoverage.weather_parameter && subcoverage.weather_parameter.length > 0 && (
                        <Card className="mb-3">
                            <Card.Header>
                                <h5 className="mb-0">Paramètres météorologiques</h5>
                            </Card.Header>
                            <Card.Body>
                                <Row className="mb-3">
                                    <Col md={12}>
                                        <div className="detail-label">Paramètres météo</div>
                                        <div className="detail-value">
                                            {subcoverage.weather_parameter.map((param, idx) => (
                                                <Badge key={idx} bg="primary" className="me-2 text-capitalize">
                                                    {param}
                                                </Badge>
                                            ))}
                                        </div>
                                    </Col>
                                </Row>

                                {subcoverage.weather_parameter.includes('temperature') && (
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <div className="detail-label">Trigger température inférieur</div>
                                            <div className="detail-value">{renderValue(subcoverage.temperature_trigger_hour_inf)}</div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="detail-label">Nombre d'heures trigger température</div>
                                            <div className="detail-value">{renderValue(subcoverage.temperature_trigger_number_hours)}</div>
                                        </Col>
                                    </Row>
                                )}

                                {subcoverage.weather_parameter.includes('wind') && (
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <div className="detail-label">Trigger vent inférieur</div>
                                            <div className="detail-value">{renderValue(subcoverage.wind_trigger_hour_inf)}</div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="detail-label">Nombre d'heures trigger vent</div>
                                            <div className="detail-value">{renderValue(subcoverage.wind_trigger_number_hours)}</div>
                                        </Col>
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    <Row className="mb-3">
                        <Col md={6}>
                            <Card className="h-100">
                                <Card.Header>
                                    <h5 className="mb-0">Référence associée</h5>
                                </Card.Header>
                                <Card.Body>
                                    <div className="detail-label">Nom</div>
                                    <div className="detail-value">{renderValue(reference.name)}</div>
                                    <div className="detail-label mt-3">Code</div>
                                    <div className="detail-value">{renderValue(reference.reference_code)}</div>
                                    <div className="detail-label mt-3">Catégorie</div>
                                    <div className="detail-value">{renderValue(reference.category)}</div>
                                    <div className="detail-label mt-3">Trigger cumulé</div>
                                    <div className="detail-value">{reference.prcp_is_cumulated ? 'Oui' : 'Non'}</div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="h-100">
                                <Card.Header>
                                    <h5 className="mb-0">Partenaire</h5>
                                </Card.Header>
                                <Card.Body>
                                    <div className="detail-label">Entité</div>
                                    <div className="detail-value">{renderValue(partner.company_name, `${renderValue(partner.first_name, '')} ${renderValue(partner.last_name, '')}`)}</div>
                                    <div className="detail-label mt-3">Email</div>
                                    <div className="detail-value">{renderValue(partner.email)}</div>
                                    <div className="detail-label mt-3">Téléphone</div>
                                    <div className="detail-value">{renderValue(partner.phone_number)}</div>
                                    <div className="detail-label mt-3">Adresse</div>
                                    <div className="detail-value">{renderValue(partner.user_address)}</div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
        </div>
    );
};

export default AdminTripSubcoverageDetail;

